import { ErrorState, LoadingState } from '@/components/common';
import {
	ContentOverview,
	RecentActivity,
	StatisticsCards,
} from '@/components/dashboard';
import { FilterOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, Col, DatePicker, Row, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import { useDashboard } from '@/hooks/dashboard';

const { Title } = Typography;
const { RangePicker } = DatePicker;

const Analytics = () => {
	const { t } = useTranslation('pages');
	const {
		stats,
		loading,
		error,
		handleDateRangeChange,
		handleClearFilters,
		handleRefresh,
		isRefreshing,
	} = useDashboard();

	if (loading && !stats) {
		return <LoadingState message={t('dashboard.loadingCharts')} fullScreen />;
	}

	if (error) {
		return (
			<ErrorState
				title={t('dashboard.errorTitle')}
				message={error}
				onRetry={handleRefresh}
				retryText={t('dashboard.retry')}
			/>
		);
	}

	if (!stats) {
		return (
			<ErrorState
				title={t('dashboard.noDataTitle')}
				message={t('dashboard.noDataMessage')}
				type='warning'
			/>
		);
	}

	return (
		<div className='space-y-4'>
			<div className='flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4'>
				<div>
					<Title level={2} className='!mb-1 !font-bold !text-slate-800'>
						{t('analytics.title')}
					</Title>
					<p className='text-slate-500 text-sm'>
						{t('analytics.subtitle')}
					</p>
				</div>

				<div className='flex items-center gap-3'>
					<RangePicker
						onChange={handleDateRangeChange}
						className='w-64 border-slate-200 shadow-sm rounded-lg'
						format='YYYY-MM-DD'
					/>

					<Button
						icon={<FilterOutlined />}
						onClick={handleClearFilters}
						className='rounded-lg'
					>
						{t('dashboard.clearFilters')}
					</Button>

					<Button
						icon={<ReloadOutlined />}
						onClick={handleRefresh}
						loading={loading || isRefreshing}
						type='primary'
						className='rounded-lg bg-indigo-600 hover:bg-indigo-500'
					>
						{t('dashboard.refresh')}
					</Button>
				</div>
			</div>

			<StatisticsCards stats={stats} />

			<Row gutter={[16, 16]}>
				<Col xs={24} lg={12}>
					<ContentOverview stats={stats} />
				</Col>

				<Col xs={24} lg={12}>
					<RecentActivity stats={stats} />
				</Col>
			</Row>
		</div>
	);
};

export { Analytics };
export default Analytics;
